import { yupResolver } from "@hookform/resolvers/yup";
import QuoteModal from "Pages/ReportManager/QuoteModal";
import {
  DeleteIcon,
  Divider,
  EyeIcon,
  GKButton,
  Grid2,
  IconButton,
  NoData,
  PopConfirm,
  Show,
  StarFillIcon,
  StarIcon,
  Tab,
  Tabs,
  Typography,
  ViewPdf,
} from "components";
import dayjs from "dayjs";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import useReport from "hooks/useReport";
import DashboardLayout from "layouts/DashboardLayout";
import { groupBy, orderBy } from "lodash";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useAppSelector } from "store/store";
import { handleRemoveUnderscore } from "utils";
import { CUSTOM_AGREEMENT_MODAL_PORTFOLIO, GET_REPORTS } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import useDocumentTitle from "utils/useDocumentTitle";
import { reportValidationSchema } from "utils/validationSchema";
import GenerateReportForm from "./GenerateReportForm";

type FormValues = {
  selectedReported?: any;
  portfolio?: any[];
  from_date?: string;
  to_date?: string;
  currentOption?: string;
  optionsToInclude?: any[];
  disclaimer?: string;
  quote?: string;
  holdingsStatement?: any[];
  bankStatement?: any[];
  availableTimePeriods?: any[];
  benchmark?: string[];
  end_date?: string;
  custom_date?: { start_date?: string; end_date?: string };
  show_zero_holding?: boolean;
  optionalField?: any[];
  tradesPlusCash?: boolean;
  gcode?: string;
  instType?: string[];
  returnMetrics?: string;
  benchmarkComparisionTimePeriods?: string[];
  advisorCategory?: string;
  allPortfolio?: boolean;
  requestBody?: string;
  equityBasket?: any[];
  selectFormat?: string;
  withCash?: boolean;
};

const GenerateReport = () => {
  useDocumentTitle("Generate New Report");

  const { quoteList, benchmarkList, benchmarkLoading } = useReport();

  const userInfo = useAppSelector((state) => state?.user?.userData);

  const [tabs, setTabs] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState({
    index: 0,
    loading: false,
  });
  const [allReportsTabs, setAllReportsTabs] = useState(0);
  const [currentTab, setCurrentTab] = useState("performance");
  const [confirmationModal, setConfirmationModal] =
    useState<HTMLElement | null>(null);
  const [deleteReportId, setDeleteReportId] = useState(null);
  const [openQuoteModal, setOpenQuoteModal] = useState(false);
  const [editQuoteValue, setEditQuoteValue] = useState({});
  const [showReportPreview, setShowReportPreview] = useState("");
  const [openPdfView, setOpenPdfView] = useState(false);

  const {
    data: getAllReportsData,
    isFetching,
    refetch,
  } = useFetchQuery({
    key: ["GET_ALL_GENERATE_REPORT_LIST"],
    route: GET_REPORTS,
  });

  const loadingValue = () => {
    setSelectedIndex((prev: any) => {
      return {
        ...prev,
        loading: false,
      };
    });
  };

  const buildReportPayload = (items: any[], format?: string) => {
    const directPortfolioIds: number[] = [];
    const clientPortfolioIds: number[] = [];
    const familyPortfolioIds: number[] = [];
    const clientIds: number[] = [];
    const familyIds: number[] = [];

    items.forEach((item) => {
      if (item.type === "portfolio") {
        directPortfolioIds.push(item.id);
      }

      if (item.type === "client") {
        clientIds.push(item.id);
        item.portfolios?.forEach((p: any) => {
          if (p?.id) clientPortfolioIds.push(p.id);
        });
      }

      if (item.type === "family") {
        familyIds.push(item.id);
        item.portfolios?.forEach((p: any) => {
          if (p?.id) familyPortfolioIds.push(p.id);
        });
      }
    });

    const allPortfolioIds = [
      ...new Set([
        ...directPortfolioIds,
        ...clientPortfolioIds,
        ...familyPortfolioIds,
      ]),
    ];

    // 🔥 ZIP MODE → ALWAYS portfolio
    if (format === "zip") {
      return {
        type: "portfolio",
        portfolio: allPortfolioIds,
      };
    }

    // 🔹 Normal logic
    if (directPortfolioIds.length > 0) {
      return {
        type: "portfolio",
        portfolio: allPortfolioIds,
      };
    }

    if (clientIds.length > 0) {
      return { type: "client", client: clientIds };
    }

    if (familyIds.length > 0) {
      return { type: "family", family: familyIds };
    }

    return {};
  };

  const form = useForm<FormValues>({
    defaultValues: {
      selectedReported: {},
      portfolio: [],
      from_date: "",
      to_date: "",
      currentOption: "",
      optionsToInclude: [],
      disclaimer: "",
      quote: "",
      holdingsStatement: [],
      bankStatement: [],
      availableTimePeriods: [],
      benchmark: [],
      end_date: "",
      custom_date: { start_date: "", end_date: "" },
      show_zero_holding: false,
      optionalField: [],
      tradesPlusCash: false,
      gcode: "",
      instType: [],
      returnMetrics: "xirr",
      benchmarkComparisionTimePeriods: [],
      advisorCategory: "",
      allPortfolio: false,
      requestBody: "",
      equityBasket: [],
      selectFormat: "individual",
      withCash: false,
    },
    resolver: yupResolver(reportValidationSchema),
  });

  form.watch();

  const { mutate: handleFavorite } = useMutation({
    mutationKey: ["FAVORITE_REPORT"],
    mutationFn: (data: any) =>
      axiosInstance.patch(`/report/${data.id}/`, { isFavorite: data.favorite }),
    onSuccess: () => {
      refetch();
    },
  });

  const { mutate: handleReportDelete } = useMutation({
    mutationKey: ["REPORT_DELETE"],
    mutationFn: ({ id }: { id: any }) => axiosInstance.delete(`/report/${id}/`),
    onSuccess: () => {
      refetch();
    },
  });

  const generateGstReport = useMutation({
    mutationKey: ["GENERATE_REPORT"],
    mutationFn: (data: any) => axiosInstance.post(data.route, data.data),
    onSuccess: async (response) => {
      await fetch(response?.data?.url)
        .then(async (response) => {
          return response.blob();
        })
        .then((blob: any) => {
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = `${response?.data?.name}`;
          link.click();
        });
      loadingValue();
    },
    onError: () => {
      loadingValue();
    },
  });

  const { mutate } = useMutation({
    mutationKey: ["GENERATE_REPORT"],
    mutationFn: async (data: any) => {
      const response = await axiosInstance.post(data.route, data.data, {
        responseType: "arraybuffer",
        validateStatus: () => true,
      });

      if (response.status >= 400) {
        const text = new TextDecoder("utf-8").decode(
          new Uint8Array(response.data),
        );
        const errorJson = JSON.parse(text);
        throw new Error(
          errorJson?.message ?? errorJson?.detail ?? "No data found",
        );
      }

      return response;
    },
    onSuccess: async (response) => {
      try {
        const buffer = response?.data;

        // ✅ Content-Type header se file type detect karo
        const contentType = response.headers?.["content-type"] ?? "";

        const getMimeAndExtension = (ct: string, fallbackName?: string) => {
          if (
            ct.includes("spreadsheet") ||
            ct.includes("excel") ||
            ct.includes("xlsx")
          ) {
            return {
              mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              ext: "xlsx",
            };
          }
          if (ct.includes("xls")) {
            return { mime: "application/vnd.ms-excel", ext: "xls" };
          }
          if (ct.includes("zip")) {
            return { mime: "application/zip", ext: "zip" };
          }
          if (ct.includes("csv")) {
            return { mime: "text/csv", ext: "csv" };
          }
          // fallback: filename extension se guess karo
          if (fallbackName?.endsWith(".xlsx"))
            return {
              mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              ext: "xlsx",
            };
          if (fallbackName?.endsWith(".xls"))
            return { mime: "application/vnd.ms-excel", ext: "xls" };
          if (fallbackName?.endsWith(".csv"))
            return { mime: "text/csv", ext: "csv" };
          // default PDF
          return { mime: "application/pdf", ext: "pdf" };
        };

        const isMultiple = form.getValues("portfolio")?.length > 1;
        const reportName = form.getValues("selectedReported")?.name;

        if (!isMultiple) {
          // JSON response check (S3 URL case)
          let parsedJson: { url: string; name: string; id: number } | null =
            null;
          try {
            const text = new TextDecoder("utf-8").decode(
              new Uint8Array(buffer),
            );
            const parsed = JSON.parse(text);
            if (parsed?.url) parsedJson = parsed;
          } catch {
            parsedJson = null;
          }

          if (parsedJson?.url) {
            // ✅ S3 URL se fetch karo — response headers se type detect karo
            const fetchResponse = await fetch(parsedJson.url);
            const fetchedContentType =
              fetchResponse.headers.get("content-type") ?? "";
            const { ext } = getMimeAndExtension(
              fetchedContentType,
              parsedJson.name,
            );
            const blob = await fetchResponse.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            // ✅ Correct extension use karo
            const baseName =
              parsedJson.name?.replace(/\.[^/.]+$/, "") ?? "report";
            link.download = `${baseName}.${ext}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000);
          } else {
            // ✅ Direct binary — content-type se detect karo
            const { mime, ext } = getMimeAndExtension(
              String(contentType),
              reportName,
            );
            const blob = new Blob([buffer], { type: mime });
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            const baseName = reportName?.replace(/\.[^/.]+$/, "") ?? "report";
            link.download = `${baseName}.${ext}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000);
          }
        } else {
          // Multiple portfolios — ZIP
          const blob = new Blob([buffer], { type: "application/zip" });
          const zipUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = zipUrl;
          link.download = reportName ? `${reportName}.zip` : "reports.zip";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => window.URL.revokeObjectURL(zipUrl), 5000);
        }
      } catch (err) {
        console.error("Download error:", err);
      } finally {
        loadingValue();
      }
    },
    onError: (error: any) => {
      const message = error?.message ?? "Something went wrong";
      toast.error(message);
      loadingValue();
    },
  });

  //  const { mutate } = useMutation({
  //   mutationKey: ["GENERATE_REPORT"],
  //   mutationFn: (data: any) =>
  //     form.getValues("portfolio")?.length === 1
  //       ? axiosInstance.post(data.route, data.data)
  //       : axiosInstance.post(data.route, data.data, {
  //           responseType: "arraybuffer",
  //         }),
  //   onSuccess: async (response) => {
  //     if (form.getValues("portfolio")?.length === 1) {
  //       await fetch(response?.data?.url)
  //         .then(async (response) => {
  //           return response.blob();
  //         })
  //         .then((blob: any) => {
  //           const link = document.createElement("a");
  //           link.href = window.URL.createObjectURL(blob);
  //           link.download = `${response?.data?.name}`;
  //           link.click();
  //         });
  //     } else {
  //       const url = window.URL.createObjectURL(
  //         new Blob([response?.data], { type: "application/zip" }),
  //       );
  //       const link = document.createElement("a");
  //       link.href = url;
  //       link.download = `${form.getValues("selectedReported")?.name}.zip`;
  //       document.body.appendChild(link);
  //       link.click();
  //       link.parentNode.removeChild(link);
  //     }
  //     loadingValue();
  //   },
  //   onError: () => {
  //     loadingValue();
  //   },
  // });

  const { mutate: handleGenerateAUMGrowthReport } = useMutation({
    mutationKey: ["GENERATE_REPORT"],
    mutationFn: (data: any) => axiosInstance.post(data.route, data.data),
    onSuccess: async (response) => {
      await fetch(response?.data?.url)
        .then(async (response) => {
          return response.blob();
        })
        .then((blob: any) => {
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = `${response?.data?.name}`;
          link.click();
        });

      loadingValue();
    },
    onError: () => {
      loadingValue();
    },
  });

  const { data: equityBasketData } = useFetchQuery({
    key: ["GET_ALL_EQUITY_BASKET"],
    route: CUSTOM_AGREEMENT_MODAL_PORTFOLIO,
  });

  const handleFormSubmit = async (values: any) => {
    delete values.name;

    if (
      values.to_date &&
      values.from_date &&
      dayjs(values.to_date).isBefore(values.from_date)
    ) {
      toast.error("End date cannot be greater than start date");
      return false;
    }
    setSelectedIndex((prev: any) => {
      return {
        ...prev,
        loading: true,
      };
    });
    const { selectedReported } = values;

    switch (selectedReported.reportName) {
      // case "custom_report":
      //   return mutate({
      //     data: {
      //       portfolio: values?.portfolio
      //         ?.map((da: any) => da?.portfolios)
      //         ?.flat()
      //         ?.map((d: any) => d?.id),
      //       end_date: values?.end_date,
      //       custom_date: {
      //         start_date: values.custom_date.start_date,
      //         end_date: values.custom_date.end_date,
      //       },
      //     },
      //     route: `/report/${values?.selectedReported?.id}/generate/`,
      //   });

      case "stock_wise_holdings_report":
        return mutate({
          data: {
            gcode: values?.gcode,
            to_date: values?.to_date,
            format: selectedIndex.index === 0 ? "pdf" : "excel",
          },
          route: `/report/${values?.selectedReported?.id}/generate/`,
        });

      case "aum_growth_report": {
        const payload = buildReportPayload(
          values.portfolio,
          values.selectFormat,
        );

        return handleGenerateAUMGrowthReport({
          data: {
            type: payload.type,
            portfolio: payload.portfolio,
            client: payload.client,
            family: payload.family,
            from_date: values?.from_date,
            instType: values?.instType,
            to_date: values?.to_date,
            format: selectedIndex.index === 0 ? "pdf" : "excel",
            end_date: values?.end_date,
            allPortfolio: values.allPortfolio,
            withCash: values.withCash,
          },
          route: `/report/${values?.selectedReported?.id}/generate/`,
        });
      }

      case "gst_report":
        return generateGstReport.mutate({
          data: {
            from_date: values?.from_date,
            to_date: values?.to_date,
            format: selectedIndex.index === 0 ? "pdf" : "excel",
            companyId: userInfo?.companyId,
            basket: Array.isArray(values?.equityBasket)
              ? values.equityBasket
              : [],
          },
          route: `/report/${values?.selectedReported?.id}/generate/`,
        });

      case "custom_report":
      case "benchmark_performance_report":
      case "historical_valuation_report":
      case "xirr_report":
      case "portfolio_summary_detailed_report":
      case "holding_period_report":
      case "transaction_report":
      case "unrealized_capital_gain_detailed_report":
      case "unrealized_capital_gain_summary_report":
      case "capital_gain_detailed_report": {
        const payload = buildReportPayload(
          values.portfolio,
          values.selectFormat,
        );

        return mutate({
          data: {
            type: payload.type,
            portfolio: payload.portfolio,
            client: payload.client,
            family: payload.family,
            from_date: values?.from_date,
            to_date: values?.to_date,
            end_date: values?.end_date,
            // Add custom_report support
            custom_date:
              selectedReported.reportName === "custom_report"
                ? {
                    start_date: values.custom_date.start_date,
                    end_date: values.custom_date.end_date,
                  }
                : undefined,
            instType: values?.instType,
            benchmark: values?.benchmark,
            returnMetrics: values?.returnMetrics,
            format: selectedIndex.index === 0 ? "pdf" : "excel",
          },
          route: `/report/${values?.selectedReported?.id}/generate/`,
        });
      }
      default:
        return mutate({
          data: {
            portfolio: values?.portfolio
              ?.map((da: any) => da?.portfolios)
              ?.flat()
              ?.map((d: any) => d?.id),
            from_date: values?.from_date,
            instType: values?.instType,
            to_date: values?.to_date,
            format: selectedIndex.index === 0 ? "pdf" : "excel",
            end_date: values?.end_date,
            benchmark: values?.benchmark,
            returnMetrics: values?.returnMetrics,
          },
          route: `/report/${values?.selectedReported?.id}/generate/`,
        });
    }
  };

  const handleReturnSortKey = (val: string) => {
    switch (val) {
      case "performance":
        return "a";
      case "transactions":
        return "b";
      case "capital_gain":
        return "c";
      default:
        return "d";
    }
  };

  return (
    <DashboardLayout title="Generate New Report" loading={isFetching}>
      <FormProvider {...form}>
        <form
          id="report-form"
          onSubmit={form.handleSubmit((values) => handleFormSubmit(values))}
        >
          <Grid2 container spacing={2}>
            <Grid2 size={12} container>
              <Grid2 size={6}>
                <Tabs onChange={(e, val) => setTabs(val)} value={tabs}>
                  <Tab label="Favorites" />
                  <Tab label="All Reports" />
                </Tabs>
                <Divider />
              </Grid2>
            </Grid2>
            <Show.When isTrue={tabs === 1}>
              <Grid2 size={12} display={"flex"}>
                <Tabs
                  variant="standard"
                  className="light"
                  TabIndicatorProps={{ style: { display: "none" } }}
                  onChange={(e, val) => setAllReportsTabs(val)}
                  value={allReportsTabs}
                >
                  {orderBy(
                    Object.entries(
                      groupBy(
                        getAllReportsData?.data?.data?.filter(
                          (dataItem: any) => dataItem.reportType !== "template",
                        ),
                        "reportType",
                      ),
                    ).map(([key]) => ({
                      name: handleRemoveUnderscore(key),
                      value: key,
                      order: handleReturnSortKey(key),
                    })),
                    ["order"],
                    ["asc"],
                  )?.map((dataItem: any, index: number) => {
                    return (
                      <Tab
                        key={dataItem?.name}
                        label={dataItem?.name}
                        value={index}
                        onClick={() => setCurrentTab(dataItem?.value)}
                      />
                    );
                  })}
                </Tabs>
              </Grid2>
            </Show.When>
            <Grid2 size={5.5}>
              <Show.When isTrue={tabs === 1}>
                <Typography fontWeight={600} fontSize={18} mb={2}>
                  Select Report Type{" "}
                </Typography>
              </Show.When>
              {(tabs === 0
                ? getAllReportsData?.data?.data?.filter((data: any) =>
                    tabs === 0 ? data?.isFavorite : !data?.isFavorite,
                  )
                : getAllReportsData?.data?.data?.filter(
                    (data: any) => data?.reportType === currentTab,
                  )
              )?.length === 0 ? (
                <NoData
                  customImg={
                    <img loading="lazy" alt="" src="/LandingPage/noFav.svg" />
                  }
                  text="No Favourites Added"
                  subText="Select the star icon in the reports to add them to your favorite reports."
                />
              ) : (
                (tabs === 0
                  ? getAllReportsData?.data?.data?.filter((data: any) =>
                      tabs === 0 ? data?.isFavorite : !data?.isFavorite,
                    )
                  : getAllReportsData?.data?.data?.filter(
                      (data: any) => data?.reportType === currentTab,
                    )
                )?.map((item: any) => {
                  return (
                    <GKButton
                      onClick={() => {
                        form.reset();
                        form.setValue("selectedReported", item);
                      }}
                      key={item?.id}
                      fullWidth
                      variant={"outlined"}
                      color="dark"
                      style={{
                        padding: 8,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 8,
                        background:
                          form.getValues("selectedReported")?.id === item?.id
                            ? "var(--primary-50)"
                            : "#fff",
                      }}
                      endIcon={
                        <div>
                          <IconButton>
                            <EyeIcon
                              color={"var(--primary-900)"}
                              width={16}
                              height={16}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowReportPreview(item?.sampleFile);
                                setOpenPdfView(true);
                              }}
                            />
                          </IconButton>
                          <Show.When isTrue={item.reportType !== "template"}>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFavorite({
                                  id: item.id,
                                  favorite: !item.isFavorite,
                                });
                              }}
                            >
                              {!item.isFavorite ? (
                                <StarIcon
                                  width={16}
                                  height={16}
                                  color={"var(--neutral-100)"}
                                />
                              ) : (
                                <StarFillIcon
                                  color={"var(--primary-900)"}
                                  width={16}
                                  height={16}
                                />
                              )}
                            </IconButton>
                          </Show.When>
                          <Show.When isTrue={item.reportType === "template"}>
                            <IconButton
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteReportId(item.id);
                                setConfirmationModal(e.currentTarget);
                              }}
                            >
                              <DeleteIcon width={16} height={16} />
                            </IconButton>
                          </Show.When>
                        </div>
                      }
                    >
                      <Typography
                        whiteSpace={"nowrap"}
                        fontWeight={400}
                        fontSize={14}
                        textTransform={"capitalize"}
                      >
                        {item?.name}
                      </Typography>
                    </GKButton>
                  );
                })
              )}
            </Grid2>
            <Grid2 size={0.5} display={"flex"} justifyContent={"center"}>
              <Divider orientation="vertical" />
            </Grid2>
            <Show.When isTrue={form.getValues("selectedReported")}>
              <Grid2 size={6}>
                <GenerateReportForm
                  selectedIndex={selectedIndex}
                  setSelectedIndex={setSelectedIndex}
                  quoteList={quoteList}
                  benchmarkList={benchmarkList}
                  benchmarkLoading={benchmarkLoading}
                  setOpenQuoteModal={setOpenQuoteModal}
                  setEditQuoteValue={setEditQuoteValue}
                  equityBasketData={equityBasketData}
                />
              </Grid2>
            </Show.When>

            <Show.When isTrue={Boolean(confirmationModal)}>
              <PopConfirm
                anchorEl={confirmationModal}
                open={Boolean(confirmationModal)}
                setOpen={setConfirmationModal}
                onSuccess={() => handleReportDelete({ id: deleteReportId })}
              />
            </Show.When>
          </Grid2>
        </form>
      </FormProvider>

      <Show.When isTrue={openQuoteModal}>
        <QuoteModal
          open={openQuoteModal}
          setOpen={setOpenQuoteModal}
          editQuoteValue={editQuoteValue}
        />
      </Show.When>

      <Show.When isTrue={openPdfView}>
        <ViewPdf
          open={openPdfView}
          setOpen={setOpenPdfView}
          file={showReportPreview}
        />
      </Show.When>
    </DashboardLayout>
  );
};

export default GenerateReport;
